import type { UnitsPreference } from "./schemas/profile.js";

// Unit conversion + display helpers.
//
// **Storage rule (non-negotiable):** every weight in the system is persisted
// in kilograms, with up to 3 decimal places (i.e. gram-precision). Users who
// pick `unitsPreference: "lb"` see and type pounds; the conversion happens
// at the UI edge. Never store both.
//
// Why on-demand conversion matters: gym machines come in either unit
// (a leg-press in lb, a free-weight rack in kg). A user reading weight off
// a machine plate needs to enter what they see, not what they prefer to
// view in. The input field will offer a per-row "kg/lb" toggle even when
// the profile preference is the other unit, so we always convert and store
// in kg.
//
// The IPF/IWF conversion factor is 0.45359237 (the international avoirdupois
// pound, exact since 1959). Anything coarser (0.4536) accumulates >0.1 kg
// drift over a few hundred conversions.

const KG_PER_LB = 0.45359237;
const LB_PER_KG = 1 / KG_PER_LB;

// ── The default, and the bounds, each written ONCE (CN-26 wave 3) ────────
//
// ⛔ Both used to be literals repeated at every point that needed them, which
// is the shape of the defect he named: "se domani introduciamo una nuova
// unità di misura siamo cotti". A default spelled `?? "kg"` in eight places
// and a range spelled `-500`/`2000` in eleven cannot be changed — they can
// only be changed in most places.
//
// ⚠️ They are CONSTANTS here, not zod fragments, on purpose. This module
// imports nothing and the schemas import it; handing it `z` to share a shape
// would invert that for no gain, because each schema already names its own
// field for a good reason (`SetWeightKg` and `WeightKg` are the same range on
// two different entities). What can silently drift is the NUMBER, so the
// number is what has one home. Same posture as `tag.ts`'s
// `NAMESPACED_VALUE_MAX`, which `workout.ts` already imports.

/**
 * The unit an athlete gets before they have chosen one.
 *
 * ⛔ It is not "kg because kg is canonical": storage is always kg regardless,
 * and this is a DISPLAY default. They coincide today and the constant is what
 * keeps them from being assumed to be the same decision tomorrow.
 *
 * ⚠️ Most `?? DEFAULT_UNITS_PREFERENCE` in this repo would be dead code: a
 * parsed `Profile` carries `.default("kg")` and mobile's `ProfileSnapshot`
 * types the field non-optional. Reach for this only where the profile is
 * genuinely absent, a document still loading, or another athlete's.
 */
export const DEFAULT_UNITS_PREFERENCE = "kg" as const;

/** The athlete's display unit, from the one field that holds it.
 *  ⛔ WHY THIS IS A FUNCTION AND NOT SIX COPIES OF ONE LINE. The console read
 *  `(doc?.unitsPreference as UnitsPreference | undefined) ?? "kg"` inline
 *  at six call sites, and the seventh and eighth never got written: the
 *  session detail panel and the catalog browser both passed a literal `"kg"`
 *  while `doc` sat in scope two lines above. An athlete in lb read weights
 *  that were wrong by 2.2×, and nothing failed: a hardcoded default is a
 *  valid value of the type. It is the SAME regression Antenore caught on
 *  2026-05-10 (1.23.2 shipped a hardcoded 'kg'), returning by the route that
 *  makes it easy, a derivation you have to remember to copy.
 *  ⭐ The `?? "kg"` fallback stays: the doc is genuinely null while loading,
 *  and kg is the family default. What is gone is the chance to skip the read.
 *  ⚠️ This is the DEFAULT unit only. The per-row kg/lb chip on a set is an
 *  OVERRIDE that dies with its row and must never write back here.
 *  Lives in shared (CN-37) so the mobile coach twin reads the same resolver.
 *  [[feedback_unit_preference_pattern]] */
export function displayUnitOf(
  doc: { unitsPreference?: unknown } | null | undefined,
): UnitsPreference {
  return (
    (doc?.unitsPreference as UnitsPreference | undefined) ??
    DEFAULT_UNITS_PREFERENCE
  );
}

/**
 * Bounds for a canonical (kg) weight, as the schemas validate it.
 *
 * ⛔ Three ranges, not one, and collapsing them would be wrong rather than
 * tidy — they answer different questions:
 *  - `WEIGHT_KG` — what can sit on a bar. Never negative.
 *  - `SIGNED_WEIGHT_KG` — the same, plus assistance: a band that takes 40 kg
 *    OFF a pull-up is a real, negative extra, which is why the floor is
 *    -500 and not 0.
 *  - `E1RM_KG` — a number a FORMULA produced, so its ceiling is higher than
 *    anything liftable on purpose: Epley on a bad rep count can overshoot,
 *    and clamping an estimate at the lifting ceiling would hide the input
 *    error instead of storing it.
 */
export const WEIGHT_KG_MIN = 0;
export const WEIGHT_KG_MAX = 2000;
export const SIGNED_WEIGHT_KG_MIN = -500;
export const SIGNED_WEIGHT_KG_MAX = WEIGHT_KG_MAX;
export const E1RM_KG_MIN = 0;
export const E1RM_KG_MAX = 5000;

export const lbToKg = (lb: number): number => lb * KG_PER_LB;
export const kgToLb = (kg: number): number => kg * LB_PER_KG;

// ── The two primitives (CN-26) ───────────────────────────────────────────
//
// ⛔ The split exists because ONE function used to do both jobs, and that is
// the whole defect: `fromCanonicalKg` converts AND rounds for display, so a
// caller that used it to TRANSPORT a value (rather than to show one) lost
// data — 100 kg → 220.5 lb → 100.017 kg (LG-85). The rule that comes out of
// it, and the one to remember: **the displayed value is not the value.**
//
// ⚠️ Splitting them does NOT by itself cure LG-85, and it was measured: once
// `220.5` is on screen, the original 100 is gone — 220.5 lb genuinely IS
// 100.017 kg, and no rounding rule recovers a number it was never told. The
// cure is to remember the canonical value instead of re-deriving it from the
// shown text (see `calculatorEngine.ts`). These two make that possible; they
// don't do it. Full census + wave plan: `next/weight-units-census.md`.

/**
 * Change a weight's unit. **Never rounds** — that is `roundWeight`'s job, and
 * keeping them apart is the point: this is what you call to carry a value
 * from one unit to another without spending precision on the trip.
 *
 * Same-unit is the identity, not a multiply-then-divide: `convertWeight(v,
 * 'kg', 'kg')` must return `v` bit-for-bit or every no-op call would drift.
 */
/**
 * The units an athlete can cycle through, in tap order.
 *
 * ⛔ A LIST, not a pair, and that is the whole point (LG-93, his words: «ogni
 * tap cambia unità a rotazione — considera il caso in cui avremo più unità»).
 * With two entries a cycling control behaves exactly like a toggle, so nothing
 * is lost today; with three it keeps working without a rewrite, because no
 * caller says "the other one".
 *
 * ⛔ It does NOT widen `UnitsPreference`: adding a third unit is a question he
 * has open, and a value nobody produces would be an abstraction without a
 * caller. This only makes the eventual widening a one-line edit HERE instead of
 * a hunt through every control that toggles.
 */
export const UNIT_CYCLE = ["kg", "lb"] as const;

/** The next unit after `unit`, wrapping. */
export const nextUnit = (unit: "kg" | "lb"): "kg" | "lb" => {
  const i = UNIT_CYCLE.indexOf(unit);
  return UNIT_CYCLE[(i + 1) % UNIT_CYCLE.length] ?? UNIT_CYCLE[0];
};

export const convertWeight = (
  value: number,
  from: "kg" | "lb",
  to: "kg" | "lb",
): number => {
  if (from === to) return value;
  return to === "kg" ? lbToKg(value) : kgToLb(value);
};

/**
 * Round a weight, **saying what the rounding is for**. The purpose is the
 * argument because the same intention was written by hand in places that
 * never agreed on the number.
 *
 * - `storage` — gram precision (3 dp), the canonical persisted form.
 * - `display` — what a human reads off a scale or types onto a bar: kg keeps
 *   grams so a typed plate value round-trips exactly, lb rounds to 1 dp
 *   (typical plate granularity is 1.25 kg ≈ 2.756 lb, but most lifters think
 *   in 0.5-lb steps).
 * - `estimate` — a weight a FORMULA produced, not a scale: 1 dp in either
 *   unit. Epley carries ~5% inherent error, so "90.417 kg e1RM" is theatre.
 *
 * ⛔ THE PURPOSE IS A PRECISION, NEVER A GRID. This function answers "how
 * many digits does this number deserve", never "which values can actually be
 * loaded" — that second question depends on the athlete's plate inventory and
 * on the loading shape, neither of which this function knows or should.
 * `roundLoadable.ts` / `roundingStepFor` own it. Keeping the two apart is the
 * whole reason there is no `plate` purpose here; an earlier draft of this
 * comment promised one, and wave 1 measured it wrong:
 *   - `plateCalculator.ts:81` (`round3`) reads like a plate rule and is not.
 *     It is gram precision used DEFENSIVELY inside the packing algorithm, and
 *     it is applied to adimensional quantities too (`:259` rounds
 *     `remaining / entry.size`, a plate COUNT). It stays local.
 *   - `pr.ts:100` (`roundToHalfKg`) is the one that genuinely means "a value
 *     you can load" — and that makes it a grid, so it belongs with
 *     `roundLoadable`, not here. `LG-93` is where it gets its answer.
 *
 * The names above stay separate on purpose (different questions), but their
 * ARITHMETIC is one formula: `roundTo` below is what `roundWeight`, `round3`,
 * `roundChainKg` and `calculatorEngine.ts`'s two inline roundings all call
 * now (DS-35 wave 2). A domain name earns its own function; a rounding
 * formula does not earn a second copy.
 */

/**
 * Round `value` to `digits` decimal places. The one place this arithmetic
 * lives (DS-35): every purpose-named rounder above calls this instead of
 * repeating `Math.round(x * 10**n) / 10**n`, so a threshold check can also
 * run it BEFORE comparing, and agree with what the display will show (see
 * `formatWeightCompact` / `formatImpulseCompact` below, and history's
 * distance-unit switch in `packages/ui/src/Logger/_HistorySetLine.tsx`).
 * A value that rounds UP to the threshold at its display precision (e.g.
 * 999.996 → "1000" at 0 decimals, 9999.96 → "10000.0" at 1) used to read as
 * if it never crossed it.
 */
export const roundTo = (value: number, digits: number): number => {
  const factor = 10 ** digits;
  return Math.round(value * factor) / factor;
};

export type RoundPurpose = "storage" | "display" | "estimate";

export const roundWeight = (
  value: number,
  unit: "kg" | "lb",
  purpose: RoundPurpose,
): number => {
  if (purpose === "storage") return roundTo(value, 3);
  // `estimate` is unit-independent on purpose: one decimal is one decimal,
  // and a formula's error swamps the difference between a kg and an lb digit.
  if (purpose === "estimate") return roundTo(value, 1);
  return unit === "kg" ? roundTo(value, 3) : roundTo(value, 1);
};

/**
 * Convert a user-entered weight (in any unit) to the canonical kg form
 * we persist. Pass the unit the user typed in, not their profile
 * preference — those can differ per row.
 */
export const toCanonicalKg = (value: number, unit: "kg" | "lb"): number =>
  roundWeight(convertWeight(value, unit, "kg"), "kg", "storage");

/**
 * Format a kg-stored weight for display in the user's preferred unit.
 * Returns a number, not a string — the i18n layer handles digit grouping
 * via Intl.NumberFormat at render time.
 *
 * ⛔ This ROUNDS, so it is for showing, never for transporting: a value that
 * comes back out of here has already spent precision. To move a weight
 * between units and keep it, call `convertWeight`.
 */
export const fromCanonicalKg = (
  kg: number,
  display: "kg" | "lb",
): number =>
  roundWeight(convertWeight(kg, "kg", display), display, "display");

// ── The unit as a human reads it (CN-26 wave 2) ──────────────────────────
//
// ⛔ The suffix used to be the `display` key itself — the literal `"kg"` /
// `"lb"`, byte-identical in all five shipped languages. A Russian athlete
// read «фунт» in the plate calculator's own unit label
// (`common.weightUnit_lb`) and «lb» in the sentence right under it,
// because that sentence takes `{{weight}}` already formatted by this file.
// Measured 11/09 in the LG-37b E2 a11y audit: the screen reader says one
// thing and the title says another. It is correctness for anyone using
// speech, not polish.
//
// ⛔ The label does NOT come from `Intl` (`style: "unit"`), and that was a
// deliberate call, not an oversight. Two reasons, in order of weight:
//   1. CLDR's short Russian for a pound is «фнт», while this app's own
//      translations already say «фунт» / «фунты» / «ФУНТ». Taking CLDR would
//      put a THIRD spelling of the same word on the same screen — the unit
//      words are translations, so their one home is the translation store.
//   2. `parseLocaleNumber.ts` already records that RN's Intl is uneven
//      across Hermes versions, and Android ships it from a prebuilt AAR, so
//      `style: "unit"` cannot be verified anywhere but on a device.
//
// ⛔ And it is a REQUIRED argument, not an optional one with the old literal
// as its default: an optional label would have left the defect alive and
// silent in every callsite nobody got around to, which is the one thing the
// rules forbid. Required means the typechecker is the census — it names
// every surface that shows a weight, and none can be missed.
//
// The resolution itself happens where `t()` lives (`packages/ui`), never
// here: `packages/shared` has no dependency on `@heraion/i18n` and must not
// grow one. This mirrors `shareCardFormat.ts`, where the caller builds a
// `ShareCardLabels` bundle and passes it into the pure formatter.

/** The unit's name for a human, resolved from i18n by the caller. */
export type WeightUnitLabels = Readonly<Record<"kg" | "lb", string>>;

/**
 * The pre-CN-26 literals, for the two places that genuinely have no `t()`:
 * jest cases that pin a number rather than a word. (`apps/web`, the other
 * caller, was removed with `RW-14`.)
 *
 * ⛔ Not a default. A new surface that reaches for this is choosing to show
 * an untranslated unit, and the name is meant to make that hard to do by
 * accident.
 */
export const UNTRANSLATED_UNIT_LABELS: WeightUnitLabels = { kg: "kg", lb: "lb" };

/**
 * Pretty-print with the unit suffix. Locale-aware decimal separator via
 * Intl.NumberFormat. Pass the i18n locale (e.g. `i18n.resolvedLanguage`)
 * for the right formatting; default `"en-US"` for tests/SSR.
 *
 * `labels` carries the translated unit words — see the block above for why
 * it is required and why it is not read from `Intl`.
 *
 * `maxFractionDigits` overrides the default precision (kg shows at most 2
 * decimals; lb 1). Pass `0` for *derived estimates* like
 * lean mass, where decimals are false precision (a BF%-driven value, inbox
 * 65d1a6ed). Real logged weights keep the default — don't lower it globally.
 */
export const formatWeight = (
  kg: number,
  display: "kg" | "lb",
  locale = "en-US",
  labels: WeightUnitLabels,
  maxFractionDigits: number = display === "kg" ? 2 : 1,
): string => {
  const value = fromCanonicalKg(kg, display);
  const fmt = new Intl.NumberFormat(locale, {
    minimumFractionDigits: 0,
    maximumFractionDigits: maxFractionDigits,
  });
  return `${fmt.format(value)} ${labels[display]}`;
};

/**
 * Compact pretty-print for big tonnages (≥10k): "12,450 kg" → "12.5k kg".
 * Below the threshold falls through to formatWeight so small numbers
 * keep full precision. Used on the post-session share card where the
 * 9:16 PNG canvas can't fit 5-digit groups at the muscle-bar / footer
 * font sizes without overrunning the column.
 *
 * The threshold check rounds to `maxFractionDigits` first (DS-35): that is
 * the precision `formatWeight` below would actually render, so a value that
 * only reaches 10,000 after that rounding still switches to the compact form
 * instead of printing a rounded "10000.0".
 */
export const formatWeightCompact = (
  kg: number,
  display: "kg" | "lb",
  locale = "en-US",
  labels: WeightUnitLabels,
  maxFractionDigits: number = display === "kg" ? 2 : 1,
): string => {
  const value = fromCanonicalKg(kg, display);
  if (Math.abs(roundTo(value, maxFractionDigits)) < 10_000)
    return formatWeight(kg, display, locale, labels, maxFractionDigits);
  const compact = Math.round(value / 100) / 10;
  const fmt = new Intl.NumberFormat(locale, {
    minimumFractionDigits: 0,
    maximumFractionDigits: 1,
  });
  return `${fmt.format(compact)}k ${labels[display]}`;
};

// The single rounding step behind both `formatVolume` and `volumeReadsZero`,
// so the guard and the printed number cannot diverge. Converts WITHOUT the
// 1 dp round of `fromCanonicalKg` (220.46 lb -> 220.5 -> 221 would double-round).
const volumeWhole = (kg: number, display: "kg" | "lb"): number =>
  Math.round(convertWeight(kg, "kg", display));

/**
 * Pretty-print an aggregate training volume (Σ reps × weight, across a
 * set/exercise/session/muscle/week) in the user's preferred unit.
 *
 * Volume is a derived sum, not a scale reading or a plate weight —
 * `formatWeight`'s default precision (2 dp for kg, 1 for lb)
 * is false precision on a summed total, and worse,
 * it means the *same* number reads with a different decimal count in kg
 * (2 dp) than in lb (1 dp) depending on which of the volume callsites
 * formats it (DS-3). Volume always renders as a whole number, both units
 * (DESIGN.md Decision 3, PG-64), including the compact form above 10,000
 * ("55k kg", never "55.2k kg").
 */
export const formatVolume = (
  kg: number,
  display: "kg" | "lb",
  locale = "en-US",
  labels: WeightUnitLabels,
): string => {
  const whole = volumeWhole(kg, display);
  const fmt = new Intl.NumberFormat(locale, { maximumFractionDigits: 0 });
  if (Math.abs(whole) < 10_000) return `${fmt.format(whole)} ${labels[display]}`;
  return `${fmt.format(Math.round(whole / 1000))}k ${labels[display]}`;
};

/** True when `formatVolume` would print a bare "0" (PG-64): callers that
 * suppress a zero volume must test this, not `kg > 0`. */
export const volumeReadsZero = (kg: number, display: "kg" | "lb"): boolean =>
  volumeWhole(kg, display) === 0;

/**
 * Format a derived/estimated weight (e1RM, projected max, etc.) at
 * coarser precision than `formatWeight`. Epley has ~5% inherent error
 * so 3 decimals on an e1RM ("90.417 kg") is theatre — round to 1 dp.
 * Use this any time the displayed number didn't come from a scale or
 * a user typing on a plate; logged weights still go through formatWeight.
 */
export const formatE1rm = (
  kg: number,
  display: "kg" | "lb",
  locale = "en-US",
  labels: WeightUnitLabels,
): string => {
  // ⭐ The unit change goes through `convertWeight`, not a hand-written
  // ternary (CN-26): this used to re-implement `display === "kg" ? kg :
  // kgToLb(kg)` next to two other copies of the same choice. ✅ Wave 1 gave
  // the 1-dp round its `RoundPurpose`; `progress.ts` held the second copy.
  const raw = convertWeight(kg, "kg", display);
  const rounded = roundWeight(raw, display, "estimate");
  const fmt = new Intl.NumberFormat(locale, {
    minimumFractionDigits: 0,
    maximumFractionDigits: 1,
  });
  return `${fmt.format(rounded)} ${labels[display]}`;
};

// Glucose. Canonical storage is mg/dL (what meters report, integer). Much of
// Europe — Switzerland included — reads mmol/L; convert at the UI edge, same
// rule as weight. Factor 18.0182 mg/dL per mmol/L (glucose molar mass
// 180.156 g/mol). Same single-source-of-truth posture: never store both.

export type GlucoseUnit = "mgdl" | "mmol";

const MGDL_PER_MMOL = 18.0182;

export const mmolToMgdl = (mmol: number): number => mmol * MGDL_PER_MMOL;
export const mgdlToMmol = (mgdl: number): number => mgdl / MGDL_PER_MMOL;

/** User-entered glucose (in the unit they typed) → canonical mg/dL (integer,
 *  matching how meters report). */
export const toCanonicalMgdl = (value: number, unit: GlucoseUnit): number =>
  Math.round(unit === "mgdl" ? value : mmolToMgdl(value));

/** mg/dL → a display number in the chosen unit (mmol/L to 1 dp, mg/dL int). */
export const fromCanonicalMgdl = (mgdl: number, display: GlucoseUnit): number =>
  display === "mgdl" ? Math.round(mgdl) : Math.round(mgdlToMmol(mgdl) * 10) / 10;

/**
 * The precision rule for a glucose number, on its own: 0 decimals for
 * mg/dL (meters report integers), 1 for mmol/L (`fromCanonicalMgdl`'s own
 * rounding). PG-56 review (25/09): this used to be inlined ONLY inside
 * `formatGlucose` below; a caller that already has a display-unit value
 * (e.g. a chart's `yFormat`, which never sees the canonical mg/dL) had no
 * way to get the SAME rule without rebuilding it by hand. One function,
 * `formatGlucose` and every such caller share it, never a second copy of
 * "0 for mgdl, 1 for mmol".
 */
export const glucoseNumberFormat = (
  display: GlucoseUnit,
  locale = "en-US",
): Intl.NumberFormat =>
  new Intl.NumberFormat(locale, {
    minimumFractionDigits: 0,
    maximumFractionDigits: display === "mgdl" ? 0 : 1,
  });

/**
 * Pretty-print with the unit suffix, locale-aware decimal separator.
 *
 * ⛔ Deliberately NOT given the `WeightUnitLabels` treatment of CN-26 wave 2,
 * and the reason is declared rather than left to look like an oversight:
 * "mg/dL" and "mmol/L" are written the same way in the five languages we
 * ship, the way `mobile.share.impulseUnit` ("kg·s") is. Weight is the
 * opposite case — Russian genuinely writes «кг» and «фунт» — which is why
 * only the weight formatters moved. If glucose ever ships to a language that
 * transliterates the symbols, it takes the same bundle and nothing else.
 */
export const formatGlucose = (
  mgdl: number,
  display: GlucoseUnit,
  locale = "en-US",
): string => {
  const value = fromCanonicalMgdl(mgdl, display);
  const fmt = glucoseNumberFormat(display, locale);
  return `${fmt.format(value)} ${display === "mgdl" ? "mg/dL" : "mmol/L"}`;
};

/** The sign glyph of an extra load on a bodyweight set: «−» is band/machine
 *  assistance, «+» is added load. The one place that spells it (#213), so the
 *  field, the timeline, the draft card and the summary can't drift apart.
 *  The caller keeps its own "is it negative" predicate (`extra < 0` on a stored
 *  set, `extraNegative` on a draft). */
export const extraSignGlyph = (negative: boolean): "−" | "+" =>
  negative ? "−" : "+";
